import { Logger } from './logging/logger';

const logger = Logger.create('heap');

const DEFAULT_SCRIPT_ID = 'reactjs-heap';

/**
 * Provides the Heap script to inject.
 */
const getHeapScript = (id: string | number) => `
  window.heapReadyCb=window.heapReadyCb||[],window.heap=window.heap||[],heap.load=function(e,t){window.heap.envId=e,window.heap.clientConfig=t=t||{},window.heap.clientConfig.shouldFetchServerConfig=!1;var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://cdn.us.heap-api.com/config/"+e+"/heap_config.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(a,r);var n=["init","startTracking","stopTracking","track","resetIdentity","identify","getSessionId","getUserId","getIdentity","addUserProperties","addEventProperties","removeEventProperty","clearEventProperties","addAccountProperties","addAdapter","addTransformer","addTransformerFn","onReady","addPageviewProperties","removePageviewProperty","clearPageviewProperties","trackPageview"],i=function(e){return function(){var t=Array.prototype.slice.call(arguments,0);window.heapReadyCb.push({name:e,fn:function(){heap[e]&&heap[e].apply(heap,t)}})}};for(var p=0;p<n.length;p++)heap[n[p]]=i(n[p])};
  heap.load("${id}",{ enableSecureCookies: true, eventPropertiesStorage: 'localstorage', metadataStorage: 'localstorage'});
`;

/**
 * Get the script id from the environment.
 */
const getHeapScriptId = (): string | undefined => process.env.REACT_APP_HEAP_SCRIPT_ID;

type HeapProperties = {
  [key: string]: boolean | number | string,
};

type Heap = {
  identify: (memberId: string) => void;
  track: (eventName: string, eventProps?: HeapProperties) => void;
  addUserProperties: (properties: HeapProperties) => void;
  addEventProperties: (properties: HeapProperties) => void;
  clearEventProperties: () => void;
  resetIdentity: () => void;
}

/**
 * Initializes Heap tracking.
 */
const initialize = () => {
  const scriptId = getHeapScriptId();
  if (!scriptId) {
    return;
  }

  if (!document) {
    return;
  }

  const hasScript = !!document.getElementById(DEFAULT_SCRIPT_ID);
  if (hasScript) {
    return;
  }

  const script = document.createElement('script');

  script.innerHTML = getHeapScript(scriptId);
  script.type = 'text/javascript';
  script.id = DEFAULT_SCRIPT_ID;
  script.async = true;

  document.body.appendChild(script);
};

/**
 * Sets the member's membership `Plan` on their Heap user profile and on every
 * event Heap sends from now on (including autocaptured ones).
 */
const setPlan = (plan: string) => {
  if (!global.heap) {
    return;
  }

  try {
    global.heap.addUserProperties({ Plan: plan });
    global.heap.addEventProperties({ Plan: plan });
  } catch (err) {
    logger.error(Logger.format('Failed to set Heap Plan', err));
  }
};

/**
 * Clears the identity and event properties so the next member on this browser
 * does not inherit them (event properties persist in localStorage).
 */
const reset = () => {
  if (!global.heap) {
    return;
  }

  try {
    global.heap.clearEventProperties();
    global.heap.resetIdentity();
  } catch (err) {
    logger.error(Logger.format('Failed to reset Heap', err));
  }
};

/**
 * Object for manipulating Heap Analytics.
 */
const ReactHeap = {
  initialize,
  setPlan,
  reset,
};

export { ReactHeap };
export type { Heap };
